/**
 * Tabs controller with support for infinite nesting.
 * Each [data-tabs="group"] is an isolated scope.
 */
class Tabs {
    /**
     * @param {Object} options - Tabs configuration.
     * @param {string} options.groupAttr   - Data attribute value for group wrapper.
     * @param {string} options.buttonAttr  - Data attribute value for tab button.
     * @param {string} options.contentAttr - Data attribute value for tab content panel.
     * @param {string} options.activeClass - Class name toggled on buttons and panels.
     * @param {boolean} options.useHash    - Push/restore active tab via URL hash.
     * @param {string} options.hashPrefix  - Optional prefix for hash values to avoid collisions.
     */
    constructor({
        groupAttr = 'group',
        buttonAttr = 'button',
        contentAttr = 'content',
        activeClass = 'is-active',
        useHash = false,
        hashPrefix = ''
    } = {}) {
        this.groupAttr   = groupAttr;
        this.buttonAttr  = buttonAttr;
        this.contentAttr = contentAttr;
        this.activeClass = activeClass;
        this.useHash     = useHash;
        this.hashPrefix  = hashPrefix;

        /** @type {Map<HTMLElement, Function>} */
        this.listeners = new Map();

        /** @type {Function|null} */
        this._popstateHandler = null;
    }

    /**
     * Attach delegated click listeners to all tab groups.
     * If `useHash` is enabled, also restores the active tab from the current URL hash
     * and subscribes to `popstate` for back/forward navigation.
     */
    init() {
        const groups = document.querySelectorAll(`[data-tabs="${this.groupAttr}"]`);

        if (!groups.length) {
            return;
        }

        groups.forEach((group) => {
            const handler = (event) => {
                const btn = event.target.closest(`[data-tabs="${this.buttonAttr}"]`);

                if (!btn || !group.contains(btn)) {
                    return;
                }

                if (btn.closest(`[data-tabs="${this.groupAttr}"]`) !== group) {
                    return;
                }

                this.switchTab(btn, group);
            };

            group.addEventListener('click', handler);
            this.listeners.set(group, handler);
        });

        if (this.useHash) {
            this._restoreFromHash(groups);

            this._popstateHandler = () => this._restoreFromHash(groups);
            window.addEventListener('popstate', this._popstateHandler);
        }
    }

    /**
     * Remove all attached event listeners.
     */
    destroy() {
        this.listeners.forEach((handler, group) => {
            group.removeEventListener('click', handler);
        });

        this.listeners.clear();

        if (this._popstateHandler) {
            window.removeEventListener('popstate', this._popstateHandler);
            this._popstateHandler = null;
        }
    }

    /**
     * Get elements that belong directly to the given group,
     * excluding elements owned by nested groups.
     *
     * @param {HTMLElement} group - Tab group container.
     * @param {string} type - Attribute value to match (button | content).
     * @returns {HTMLElement[]}
     */
    getGroupElements(group, type) {
        return [...group.querySelectorAll(`[data-tabs="${type}"]`)]
            .filter((el) => el.closest(`[data-tabs="${this.groupAttr}"]`) === group);
    }

    /**
     * Switch active tab within a group.
     *
     * @param {HTMLElement} btn       - Clicked tab button.
     * @param {HTMLElement} group     - Owning tab group.
     * @param {boolean}     pushHash  - Whether to push the hash to the browser history.
     *                                  Set to `false` when restoring from hash to avoid
     *                                  polluting the history stack.
     */
    switchTab(btn, group, pushHash = true) {
        const targetId = btn.dataset.id;
        const buttons  = this.getGroupElements(group, this.buttonAttr);
        const contents = this.getGroupElements(group, this.contentAttr);

        buttons.forEach((el) => el.classList.remove(this.activeClass));
        contents.forEach((el) => el.classList.remove(this.activeClass));

        btn.classList.add(this.activeClass);

        const target = group.querySelector(`#${targetId}`);

        if (target) {
            target.classList.add(this.activeClass);
        }

        if (this.useHash && pushHash) {
            history.pushState(null, '', `#${this.hashPrefix}${targetId}`);
        }

        group.dispatchEvent(new CustomEvent('tabs:change', {
            bubbles : true,
            detail  : {
                group,
                button : btn,
                targetId
            }
        }));
    }

    /**
     * Restore the active tab from the current URL hash.
     * Iterates over all groups and activates the matching button without pushing a new history entry.
     *
     * @param {NodeList|HTMLElement[]} groups - All tab group elements.
     */
    _restoreFromHash(groups) {
        const raw = location.hash.slice(1);

        if (!raw) {
            return;
        }

        const targetId = this.hashPrefix
            ? raw.replace(new RegExp(`^${this.hashPrefix}`), '')
            : raw;

        for (const group of groups) {
            const btn = this.getGroupElements(group, this.buttonAttr)
                .find((el) => el.dataset.id === targetId);

            if (btn) {
                this.switchTab(btn, group, false);
            }
        }
    }
}

export { Tabs };
